<template>
    <admin-page :result="result" description="Convierte una empresa en entrenador independiente">
        <div class="form-row">
            <kimsa-text-field class="form-field" @change="onChangeEmail" :value="email" label="Email de la empresa"
                placeholder="empresa@correo.com" dense hide-details />
            <v-btn color="secondary" class="form-btn" depressed :loading="checking" :disabled="!email"
                @click="loadPlan">
                Ver qué pasaría
            </v-btn>
        </div>

        <div v-if="plan" class="plan-card">
            <div class="plan-name">{{ plan.companyName || 'Sin nombre' }}</div>
            <div class="plan-fields">
                <div class="plan-field">
                    <span>Suscripción en Stripe</span>
                    <b>{{ plan.willCancelStripe ? `se cancela (${plan.stripeStatus})` : `no se toca (${plan.stripeStatus})` }}</b>
                </div>
                <div class="plan-field">
                    <span>Entrenadores que se sueltan</span>
                    <b>{{ plan.coaches.length }}</b>
                </div>
                <div class="plan-field">
                    <span>Suscripciones a borrar</span>
                    <b>{{ plan.subscriptionsToDelete }}</b>
                </div>
                <div class="plan-field">
                    <span>Invitaciones a borrar</span>
                    <b>{{ plan.invitationsToDelete }}</b>
                </div>
            </div>

            <div v-if="plan.coaches.length" class="plan-coaches">
                <div v-for="coach in plan.coaches" :key="coach.uid" class="plan-coach">{{ coach.email }}</div>
            </div>

            <v-btn color="error" class="plan-btn mt-4" depressed block :loading="loading"
                @click="$refs.confirm.show()">
                Convertir a entrenador
            </v-btn>
        </div>

        <kimsa-confirm ref="confirm" alert-mode show-cancel title="Empresa a entrenador" :content="confirmContent"
            :persistent="false" :close-on-confirm="false" :loading="loading" @confirm="apply" />
    </admin-page>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/functions'
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { adminRouteGuard } from '@/utils/Admin'

export default {
    name: 'AdminCompanyToCoach',
    components: { KimsaTextField, KimsaConfirm, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            email: '',
            plan: null,
            checking: false,
            loading: false,
            result: null,
        }
    },
    computed: {
        confirmContent(vm = this) {
            return `Vas a convertir a <b>${vm.email.trim()}</b> en entrenador independiente: se cancela su suscripción, ${vm.plan?.coaches.length || 0} entrenador(es) quedan sueltos y se borran sus suscripciones e invitaciones. No se puede deshacer solo.`
        },
    },
    mounted() {
        const vm = this
        if (vm.$route.query.email) vm.email = vm.$route.query.email
    },
    methods: {
        onChangeEmail(email) {
            const vm = this
            vm.email = email
            vm.plan = null
            vm.result = null
        },
        async call(apply) {
            const fn = firebase.functions().httpsCallable('httpAdmin-companyToCoach')
            const { data } = await fn({ email: this.email.trim(), apply })
            return data
        },
        async loadPlan() {
            const vm = this
            vm.plan = null
            vm.result = null
            await vm.withLoading(async () => {
                try {
                    vm.plan = await vm.call(false)
                } catch (err) {
                    console.error('[adminCompanyToCoach] plan', err)
                    vm.result = { type: 'error', message: err.message || 'Error al consultar la empresa.' }
                }
            }, 'checking')
        },
        async apply() {
            const vm = this
            vm.loading = true
            vm.result = null
            try {
                const data = await vm.call(true)
                vm.result = {
                    type: 'success',
                    message: `Listo. ${vm.email} es entrenador independiente: ${data.coaches.length} entrenador(es) sueltos, ${data.subscriptionsToDelete} suscripción(es) y ${data.invitationsToDelete} invitación(es) borradas.`
                }
                vm.plan = null
                vm.email = ''
            } catch (err) {
                console.error('[adminCompanyToCoach] apply', err)
                vm.result = { type: 'error', message: err.message || 'Error al convertir la empresa.' }
            } finally {
                vm.loading = false
                vm.$refs.confirm?.hide()
            }
        },
    },
}
</script>

<style scoped>
.form-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}

.form-field {
    flex: 1;
    min-width: 240px;
}

.form-btn {
    height: 40px !important;
    min-width: 160px;
    border-radius: 8px;
}

.plan-card {
    margin-top: 20px;
    padding: 16px;
    border-radius: 12px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.plan-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-bottom: 12px;
}

.plan-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 20px;
}

.plan-field {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
    padding: 5px 0;
}

.plan-field b {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
    text-align: right;
}

.plan-coaches {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.plan-coach {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 20px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
}

.plan-btn {
    height: 40px !important;
    border-radius: 8px;
}

@media (max-width: 600px) {
    .plan-fields {
        grid-template-columns: 1fr;
    }

    .form-btn {
        width: 100%;
    }
}
</style>
